import { api, cachedGet, clearCache } from './apiClient';
import type { ApiResponse } from 'shared/types/api';

// Cache durations
const CACHE_SHORT = 30000; // 30 seconds

// Types
export interface VerificationSummary {
  status: 'unverified' | 'missing_documents' | 'pending_review' | 'rejected' | 'verified';
  company: {
    total: number;
    required: number;
    submitted: number;
    approved: number;
    rejected: number;
    pending: number;
    missing: number;
  };
  overall: {
    totalRequired: number;
    totalApproved: number;
    totalRejected: number;
    totalPending: number;
    totalMissing: number;
    percentComplete: number;
  };
  canWork: boolean;
}

export interface VerificationSubmissionHistoryEntry {
  submittedAt?: string;
  fileKey?: string;
  fileName?: string;
  status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired' | 'not_submitted';
  submittedBy?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface VerificationItem {
  _id: string;
  name: string;
  description?: string;
  type: 'document' | 'course' | 'certification' | 'training' | 'medical_exam' | 'background_check';
  isRequired: boolean;
  /** Days the approval stays valid. Used by the review modal to
   *  suggest a default expiry date when approving. */
  validityPeriod?: number;
  userStatus: 'not_submitted' | 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired';
  userVerificationId?: string;
  fileKey?: string;
  fileName?: string;
  submittedDate?: string;
  approvedDate?: string;
  rejectedDate?: string;
  rejectionReason?: string;
  expiryDate?: string;
  company?: string;
  companyName?: string;
  /** Raw ObjectId strings — used by the detail modal as a best-effort
   *  display when a name isn't available (aggregation can't populate). */
  submittedBy?: string;
  approvedBy?: string;
  rejectedBy?: string;
  /** Prior submissions/reviews in chronological order. Drives the
   *  per-verification history modal. Empty when this is the user's
   *  first submission. */
  submissionHistory?: VerificationSubmissionHistoryEntry[];
}

export type MyVerificationsResponse = ApiResponse<{
  summary: VerificationSummary;
  companyVerifications: VerificationItem[];
}>;

export type UploadUrlResponse = ApiResponse<{
  presignedPost: {
    url: string;
    fields: Record<string, string>;
  };
  key: string;
}>;

export type DocumentUrlResponse = ApiResponse<{
  url: string;
  expiresIn: number;
  fileName: string;
  mimeType: string;
}>;

export interface AuditLogEntry {
  timestamp: string;
  action: 'submitted' | 'approved' | 'rejected';
  verificationType: 'company';
  verificationName: string;
  companyName: string;
  companyId: string;
  fileKey?: string;
  fileName?: string;
  status: string;
  isCurrent?: boolean;
  reviewerName?: string;
  reviewerEmail?: string;
  rejectionReason?: string;
  expiryDate?: string;
}

export type UserAuditLogResponse = ApiResponse<{
  user: { _id: string; firstName: string; lastName: string; email: string; cedula?: string };
  entries: AuditLogEntry[];
}>;

export interface VerificationReviewer {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface VerificationSubmissionEntry {
  submittedAt: string;
  fileKey?: string;
  fileName?: string;
  status: string;
  reviewedBy?: VerificationReviewer | null;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface PendingVerification {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    cedula?: string;
  };
  verification: {
    _id: string;
    name: string;
    type: string;
    description?: string;
  };
  status: string;
  /** Ruta S3 (envío por URL prefirmada). Vacío en los envíos por GridFS. */
  fileKey: string;
  /** Id del archivo en GridFS (envío vía POST /api/files/upload). Vacío en
   *  los envíos por S3. Toda lectura de documento debe contemplar ambos. */
  documentoId?: string;
  fileName: string;
  fileMimeType: string;
  fileSize: number;
  submittedAt: string;
  company?: {
    _id: string;
    name: string;
  };
  verificationType: 'company';
  // Approval chain — populated by the BE for the review modal so the
  // supervisor can see who reviewed each attempt and why a previous
  // submission was rejected.
  approvedBy?: VerificationReviewer | null;
  approvedDate?: string;
  rejectedBy?: VerificationReviewer | null;
  rejectedDate?: string;
  rejectionReason?: string;
  expiryDate?: string;
  certificateNumber?: string;
  notes?: string;
  submissionHistory?: VerificationSubmissionEntry[];
}

export interface PendingUser {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  cedula?: string;
  pendingCount: number;
  lastSubmission: string;
}

export type UserVerificationStatus = ApiResponse<{
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    cedula?: string;
  };
  summary: VerificationSummary;
  companyVerifications: VerificationItem[];
}>;

export const userVerificationApi = {
  // User endpoints
  getMyVerifications: async (companyId?: string): Promise<MyVerificationsResponse> => {
    const params = companyId ? { companyId } : {};
    const response = await cachedGet('/user-verifications/me', params, CACHE_SHORT);
    return response as MyVerificationsResponse;
  },

  getUploadUrl: async (
    verificationType: 'company',
    verificationId: string,
    contentType: string,
    fileName: string
  ): Promise<UploadUrlResponse> => {
    const response = await api.post(
      `/user-verifications/upload-url/${verificationType}/${verificationId}`,
      { contentType, fileName }
    );
    return response as UploadUrlResponse;
  },

  submitCompanyVerification: async (
    companyId: string,
    verificationId: string,
    data: {
      fileKey: string;
      fileName: string;
      fileMimeType: string;
      fileSize: number;
      targetUserId?: string; // For admin uploads on behalf of users
    }
  ) => {
    const response = await api.post(
      `/user-verifications/company/${companyId}/${verificationId}/submit`,
      data
    );
    clearCache('/user-verifications/me');
    if (data.targetUserId) {
      clearCache(`/user-verifications/users/${data.targetUserId}`);
    }
    // Sin esto, si un admin/supervisor tiene abierta la cola de revisión
    // pendiente (o el resumen de "documentos faltantes" del roster) cuando
    // se envía este documento, el nuevo item no aparece hasta que el TTL de
    // 30s expire por su cuenta.
    clearCache('/user-verifications/pending');
    clearCache('/user-verifications/users/summary');
    return response;
  },

  getDocumentUrl: async (
    verificationType: 'company',
    userVerificationId: string
  ): Promise<DocumentUrlResponse> => {
    const response = await api.get(
      `/user-verifications/document/${verificationType}/${userVerificationId}`
    );
    return response as DocumentUrlResponse;
  },

  // Admin endpoints
  getUserVerifications: async (userId: string, companyId?: string): Promise<UserVerificationStatus> => {
    const response = await cachedGet(
      `/user-verifications/users/${userId}`,
      companyId ? { companyId } : {},
      CACHE_SHORT
    );
    return response as UserVerificationStatus;
  },

  // Batched "missing documents" summary for a whole roster in one request —
  // use instead of N getUserVerifications calls when rendering a user list.
  getUsersVerificationSummary: async (
    userIds: string[],
    companyId: string
  ): Promise<ApiResponse<Record<string, { missing: string[]; isVerified: boolean; notApplicable: boolean }>>> => {
    const response = await cachedGet(
      '/user-verifications/users/summary',
      { companyId, userIds: userIds.join(',') },
      CACHE_SHORT
    );
    return response as ApiResponse<Record<string, { missing: string[]; isVerified: boolean; notApplicable: boolean }>>;
  },

  getPendingVerifications: async (params?: {
    type?: 'company';
    page?: number;
    limit?: number;
    /** Optional comma-separated subset, e.g. 'pending,in_review,rejected'. */
    statuses?: string;
    /** Restrict to a single company (supervisor view). */
    companyId?: string;
    /** Optional comma-separated user-id list — scope the result to a
     *  specific set of users (e.g. a supervisor's direct reports). */
    users?: string;
  }) => {
    const response = await cachedGet(
      '/user-verifications/pending',
      params || {},
      CACHE_SHORT
    );
    const typedResponse = response as ApiResponse<{
      company: Array<Omit<PendingVerification, 'verificationType'>>;
    }>;

    const companyVerifications: PendingVerification[] = (typedResponse.data?.company || []).map(v => ({
      ...v,
      verificationType: 'company' as const,
    }));

    // Sort by submittedAt
    const allVerifications = companyVerifications.sort(
      (a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
    );

    return {
      success: typedResponse.success,
      data: allVerifications,
      pagination: {
        page: params?.page || 1,
        limit: params?.limit || 50,
        total: allVerifications.length,
        pages: Math.ceil(allVerifications.length / (params?.limit || 50)),
      },
    };
  },

  getPendingUsers: async (params?: {
    page?: number;
    limit?: number;
  }) => {
    const response = await cachedGet(
      '/user-verifications/pending-users',
      params || {},
      CACHE_SHORT
    );
    return response as ApiResponse<PendingUser[]>;
  },

  reviewCompanyVerification: async (
    userVerificationId: string,
    data: {
      action: 'approve' | 'reject';
      rejectionReason?: string;
      expiryDate?: string;
      // Correcciones que el revisor puede aplicar sobre el propio envío en
      // vez de rechazarlo para que la persona vuelva a subir todo. El
      // archivo de reemplazo va como documentoId (GridFS, subido por
      // fileService) — el BE no acepta un fileKey crudo.
      certificateNumber?: string;
      documentoId?: string;
      fileName?: string;
      fileMimeType?: string;
      fileSize?: number;
    }
  ) => {
    const response = await api.put(
      `/user-verifications/company/${userVerificationId}/review`,
      data
    );
    clearCache('/user-verifications');
    return response;
  },

  initializeUserVerifications: async (userId: string) => {
    const response = await api.post(
      `/user-verifications/users/${userId}/initialize`
    );
    clearCache('/user-verifications');
    return response;
  },

  getUserAuditLog: async (userId: string, params?: Record<string, string | number>): Promise<UserAuditLogResponse> => {
    const response = await cachedGet(
      `/user-verifications/users/${userId}/audit-log`,
      params || {},
      CACHE_SHORT
    );
    return response as UserAuditLogResponse;
  },

  getDocumentByKey: async (fileKey: string): Promise<ApiResponse<{ url: string; expiresIn: number }>> => {
    const response = await api.get('/user-verifications/document-by-key', { fileKey });
    return response as ApiResponse<{ url: string; expiresIn: number }>;
  },
};

export default userVerificationApi;
